import * as _ from 'lodash';
import { Component, DestroyRef, Input, OnInit, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { PeerToPeerManageService } from '../../services/peer-to-peer-manage/peer-to-peer-manage.service';
import { DomSanitizer } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { ImageService } from 'app/core/services/image.service';
import { ToastService } from 'app/toast/services/toast.service';
import { ModalP2pDetailService } from 'app/manage-p2p/services/modal-p2p-detail/modal-p2p-detail.service';
import { P2pLinkPopoverComponent } from '../p2p-link-popover/p2p-link-popover/p2p-link-popover.component';
import { P2pDetailsPopoverComponent } from '../p2p-details-popover/p2p-details-popover.component';
import { CoreModule } from 'app/core/core.module';
import { DatePipe,CurrencyPipe, CommonModule } from '@angular/common';
import { P2pDetailModalComponent } from 'app/manage-p2p/modals/p2p-detail-modal/p2p-detail-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
  standalone: true,
  selector: 'app-peer-to-peer-details-card',
  templateUrl: './peer-to-peer-details-card.component.html',
  styleUrls: ['./peer-to-peer-details-card.component.scss'],
  imports:[P2pLinkPopoverComponent,P2pDetailsPopoverComponent,CoreModule,FormsModule,CommonModule,ReactiveFormsModule,DatePipe,CurrencyPipe, P2pDetailModalComponent]
})
export class PeerToPeerDetailsCardComponent implements OnInit {
  destroyRef = inject(DestroyRef)
  private _data: WritableSignal<any> = signal(null);

  @Input()
  set data(value: any) {
    this._data.set(value);
  };
  get data(): any {
    return this._data();
  }

  public createdByName: Signal<string> = computed(() => {
    return this.data.createdByName;
  });
  
  public optionName: Signal<string> = computed(() => {
    return this.data.optionInfo?.name || 'Other';
  });

  public raised: Signal<number> = computed(() => {
    return this.data.raised;
  });

  public goal: Signal<number> = computed(() => {
    return this.data.goal;
  });

  public progress: Signal<number> = computed(() => {
    let progressValue = (this.raised()/this.goal()) * 100;

    if (this.goal() === 0 || this.raised() > this.goal()) {
      progressValue = 100;
    }

    return progressValue;
  });

  public progressNgStyle = computed(() => {
    return {
      width: `${this.progress()}%`
    }
  });

  public creationUnix: Signal<any> = computed(() => {
    return new Date(this.data.created);
  });

  public pledgePerEvent: Signal<boolean> = computed(() => {
    return this.data?.campaignData?.campaignTypeData?.pledgePerEvent || false;
  });
  
  public donationsText: Signal<string> = computed(() => {
    const transactions = this.data.transactions || [];
    const ending = transactions.length === 1 ? 'Donation' : 'Donations';
    return `${this.data.transactions.length} ${ending}`;
  });

  public pledgesText: Signal<string> = computed(() => {
    const pledges = this.data.pledges || [];
    const ending = pledges.length === 1 ? 'Pledge' : 'Pledges';
    return `${pledges.length} ${ending}`;
  });

  public isModalOpen = false;

  public donateUrlParams = {};
  public fundraiserUrlParams = {};

  public imageFile: UntypedFormControl = new UntypedFormControl(null);
  public imageDataUrl: UntypedFormControl = new UntypedFormControl(null);
  public imageUrl: UntypedFormControl = new UntypedFormControl(null);
  
  constructor(
    public peerToPeerManageService: PeerToPeerManageService,
    public imageService: ImageService,
    public domSanitizer: DomSanitizer,
    private toastService: ToastService,
    private modalP2pDetailService: ModalP2pDetailService,
  ) { }

  ngOnInit(): void {
    this.donateUrlParams = {
      url: this.data.url,
      type: 'p2p-fundraiser',
      fileName: `${this.data.displayName}_donate_QR_Code`
    }

    this.fundraiserUrlParams = {
      url: `${this.data.url}&share=true`,
      type: 'p2p-fundraiser',
      fileName: `${this.data.displayName}_share_QR_Code`
    }

    let img = this.data.profileImage || this.data.userInfo.image || "/images/org_logo_default_v2.svg";
    this.imageDataUrl.setValue(img)

    this.imageFile.valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      const windowURL = window.URL || window.webkitURL;
      const url = windowURL.createObjectURL(value);
      this.imageService.cropper({
        url: this.domSanitizer.bypassSecurityTrustUrl(url),
        id: `p2p-donor-image_${this.data.id}`,
        name: value.name,
        type: value.type,
        aspectRatio:1
      });
    });

    this.imageService.imageDataUrlSubject.pipe(
      filter(({ id }) => id === `p2p-donor-image_${this.data.id}`),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      this.imageDataUrl.setValue(result);

      if (this.data.profileImage !== undefined && this.data.profileImage !== null) {
        this.data.profileImage = result;
      } else {
        this.data.userInfo.image = result;
      }
    });

    this.imageService.imageSubject.pipe(
      filter(({ id }) => id === `p2p-donor-image_${this.data.id}`),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ result }) => {
      if(result === false) {
        this.resetImage();
      } else {
        this.imageUrl.setValue(result.imageUrl);
        this.peerToPeerManageService.p2pChangeUserImage({
          id: this.data.id,
          profileImage: result.imageUrl
        }).pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe()
        this.toastService.show('success', 'Image Updated', {delay: 150000})
      }
    });
  }

  openP2pDetailModal(activeTab: 'donations' | 'pledges' = 'donations') {
    const data = {
      fundraiserData: this.data,
      placeOfCall: 'viewDonations',
      activeTab
    }
    const modalRef = this.modalP2pDetailService.open(data);

    modalRef.onClose.pipe().subscribe(() =>{
      this.modalP2pDetailService.modalRef = null;
    })
  }

  resetImage() {
    this.imageFile.setValue(null);
    this.imageUrl.setValue('/images/blank_2x1.png');
  }
}
